import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import PrintDialog from '@/components/shared/PrintDialog';
import CustomerDebtSummaryPrintContent from './CustomerDebtSummaryPrintContent';
import { useCompanyConfig } from '@/hooks/useCompanyConfig';
import { Loader2, Download, ListTree } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

interface CustomerDebtSummaryPrintModalProps {
  open: boolean;
  onClose: () => void;
  initialCustomerIds?: string[];
}

type DatePreset = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear' | 'custom';

function getLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function CustomerDebtSummaryPrintModal({
  open,
  onClose,
  initialCustomerIds = [],
}: CustomerDebtSummaryPrintModalProps) {
  const companyConfig = useCompanyConfig(open);

  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => getLocalDateString(today), [today]);

  const firstDayOfMonthStr = useMemo(() => {
    const f = new Date();
    f.setDate(1);
    return getLocalDateString(f);
  }, []);

  const lastMonthRange = useMemo(() => {
    const now = new Date();
    const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      from: getLocalDateString(firstDayLastMonth),
      to: getLocalDateString(lastDayLastMonth),
    };
  }, []);

  const firstDayOfYearStr = useMemo(() => {
    const f = new Date(new Date().getFullYear(), 0, 1);
    return getLocalDateString(f);
  }, []);

  // Filter States
  const [preset, setPreset] = useState<DatePreset>('thisMonth');
  const [fromDate, setFromDate] = useState<string>(firstDayOfMonthStr);
  const [toDate, setToDate] = useState<string>(todayStr);
  const [showDetails, setShowDetails] = useState(false);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>(initialCustomerIds);

  const handleSelectPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'thisMonth') {
      setFromDate(firstDayOfMonthStr);
      setToDate(todayStr);
    } else if (p === 'lastMonth') {
      setFromDate(lastMonthRange.from);
      setToDate(lastMonthRange.to);
    } else if (p === 'thisYear') {
      setFromDate(firstDayOfYearStr);
      setToDate(todayStr);
    }
  };

  // Fetch report data
  const { data: reportData, isLoading, isFetching } = useQuery({
    queryKey: ['customer-debt-summary-report', fromDate, toDate, selectedCustomerIds],
    queryFn: async () => {
      return (trpcClient as any).ar.getCustomerDebtSummaryReport.query({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        customerIds: selectedCustomerIds.length > 0 ? selectedCustomerIds : undefined,
      });
    },
    enabled: open,
    staleTime: 1000 * 60,
  });

  // Handle Excel Export matching totalcongno.html
  const handleExportExcel = () => {
    if (!reportData?.results || reportData.results.length === 0) {
      toast.error('Không có dữ liệu đối soát công nợ để xuất Excel');
      return;
    }

    try {
      const summaryRows = reportData.results.map((item: any, idx: number) => ({
        'STT': idx + 1,
        'Mã KH': item.ma_kh || '',
        'Tên Khách Hàng': item.ten_kh || '',
        'Nợ Đầu Kỳ': item.no_dau_ky || 0,
        'Phát Sinh Trong Kỳ': item.phat_sinh_ky || 0,
        'Thanh Toán Trong Kỳ': item.da_thanh_toan_ky || 0,
        'Nợ Cuối Kỳ': item.no_cuoi_ky || 0,
      }));

      // Add Grand Total row
      summaryRows.push({
        'STT': 'TỔNG CỘNG',
        'Mã KH': '',
        'Tên Khách Hàng': '',
        'Nợ Đầu Kỳ': reportData.summary?.totalOpeningBalance || 0,
        'Phát Sinh Trong Kỳ': reportData.summary?.totalIncurred || 0,
        'Thanh Toán Trong Kỳ': reportData.summary?.totalPaid || 0,
        'Nợ Cuối Kỳ': reportData.summary?.totalClosingBalance || 0,
      });

      // Sheet 2: Detailed transactions
      const detailRows: any[] = [];
      reportData.results.forEach((cust: any) => {
        if (cust.details && cust.details.length > 0) {
          cust.details.forEach((d: any, dIdx: number) => {
            detailRows.push({
              'Mã KH': cust.ma_kh,
              'Tên KH': cust.ten_kh,
              'STT': dIdx + 1,
              'Ngày': d.ngay ? new Date(d.ngay).toLocaleDateString('vi-VN') : '',
              'Diễn Giải': d.dien_giai || '',
              'Số Lượng': d.so_luong || '',
              'Đơn Giá': d.don_gia || '',
              'Phát Sinh (+)': d.tang || 0,
              'Thanh Toán (-)': d.giam || 0,
              'Số Chứng Từ': d.so_ct || '',
            });
          });
        }
      });

      const workbook = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(workbook, wsSummary, 'TongHopCongNo');

      if (detailRows.length > 0) {
        const wsDetail = XLSX.utils.json_to_sheet(detailRows);
        XLSX.utils.book_append_sheet(workbook, wsDetail, 'ChiTietGiaoDich');
      }

      XLSX.writeFile(
        workbook,
        `TongHopCongNo_${fromDate}_${toDate}_${Date.now()}.xlsx`
      );
      toast.success('Đã xuất file Excel Tổng Hợp Công Nợ thành công!');
    } catch (err: any) {
      toast.error('Lỗi khi xuất file Excel: ' + (err.message || ''));
    }
  };

  if (!open) return null;

  // Custom Toolbar Controls on top of PrintDialog
  const extraControls = (
    <div className="hidden lg:flex items-center gap-2 text-xs">
      {/* Preset selector */}
      <div className="flex items-center bg-slate-100 p-0.5 rounded-md border border-slate-200">
        <button
          type="button"
          onClick={() => handleSelectPreset('thisMonth')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'thisMonth' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tháng này
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('lastMonth')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'lastMonth' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tháng trước
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('thisYear')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'thisYear' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Năm nay
        </button>
        <button
          type="button"
          onClick={() => setPreset('custom')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'custom' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tùy chọn
        </button>
      </div>

      {/* Date inputs */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 text-[11px]">
        <span className="text-slate-500">Từ:</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
        <span className="text-slate-500">Đến:</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
      </div>

      {/* Toggle show details checkbox */}
      <label className="flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 bg-slate-50 text-slate-700 border border-slate-200 rounded text-xs font-medium hover:bg-slate-100 transition-colors">
        <input
          type="checkbox"
          checked={showDetails}
          onChange={(e) => setShowDetails(e.target.checked)}
          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
        />
        <ListTree className="w-3.5 h-3.5 text-slate-500" />
        <span>Hiện chi tiết giao dịch</span>
      </label>

      {/* Orientation selector */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded border border-slate-200">
        <span className="text-slate-500 font-medium">Khổ giấy:</span>
        <select
          value={orientation}
          onChange={(e) => setOrientation(e.target.value as any)}
          className="bg-transparent border-0 text-slate-800 font-medium cursor-pointer text-xs focus:outline-hidden"
        >
          <option value="landscape">Ngang (Landscape)</option>
          <option value="portrait">Đứng (Portrait)</option>
        </select>
      </div>

      {/* Excel Export Button */}
      <button
        type="button"
        onClick={handleExportExcel}
        className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 px-2.5 py-1.5 rounded font-medium transition-colors cursor-pointer"
        title="Xuất bảng đối soát công nợ ra file Excel"
      >
        <Download className="h-3.5 w-3.5 text-emerald-600" />
        <span>Xuất Excel</span>
      </button>
    </div>
  );

  return (
    <PrintDialog
      open={open}
      onClose={onClose}
      type="custom"
      orientation={orientation}
      documentTitle={`Tong_Hop_Cong_No_${fromDate}_${toDate}`}
      extraControls={extraControls}
    >
      {isLoading || isFetching ? (
        <div className="py-24 flex flex-col items-center justify-center text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
          <p className="font-medium text-sm">Đang tổng hợp đối soát công nợ khách hàng...</p>
        </div>
      ) : (
        <CustomerDebtSummaryPrintContent
          reportData={reportData || { summary: { totalOpeningBalance: 0, totalIncurred: 0, totalPaid: 0, totalClosingBalance: 0, totalCustomers: 0 }, results: [] }}
          dateRange={{ from: fromDate, to: toDate }}
          config={companyConfig}
          showDetails={showDetails}
        />
      )}
    </PrintDialog>
  );
}

export default CustomerDebtSummaryPrintModal;
